import type { ComponentStatus } from "./dierComponenten";
import type { PensionDier } from "../pensionKlantTypes";

/**
 * De status van een onderdeel dat een geldigheidsdatum draagt: entingen, ontworming, ontvlooiing.
 *
 * <p>Drie standen uit het ontwerp — in orde, aandacht, verlopen — en de grens ertussen is een keuze die
 * hier één keer staat in plaats van per tegel. Zou elke tegelvorm zijn eigen grens trekken, dan zegt de
 * ring "aandacht" waar de statuslijst nog "in orde" zegt over hetzelfde dier.
 *
 * <p><b>Nooit behandeld is geen status.</b> Een dier waarvan niets bekend is krijgt geen bolletje: een
 * groen vinkje bij "nooit ontwormd" leest als een controle die gedaan is, en een rood kruis leest als
 * een overschreden termijn die er niet is. Leeg betekent leeg, en het onderdeel zegt zelf dat er nog
 * niets geregistreerd is.
 */

/**
 * Hoeveel dagen van tevoren een aflopende geldigheid aandacht vraagt.
 *
 * <p>Dertig dagen, gelijk aan de termijn die de tarieflijst voor "Nieuw" aanhoudt en aan wat een pension
 * nodig heeft om een afspraak bij de dierenarts te maken. Korter en het is te laat om nog iets te
 * regelen; langer en alles staat maandenlang op oranje, waarmee de kleur niets meer zegt.
 */
export const AANDACHT_DAGEN = 30;

/**
 * De status bij een geldig-tot-datum.
 *
 * @param geldigTot de dag tot en met wanneer het dier gedekt is; leeg = nooit behandeld
 * @param vandaag   de dag waartegen gerekend wordt
 * @returns de status, of {@code undefined} als er niets bekend is
 */
export function statusVanGeldigheid(
  geldigTot: string | null | undefined,
  vandaag: Date,
): ComponentStatus | undefined {
  if (!geldigTot) {
    return undefined;
  }
  const tot = new Date(`${geldigTot.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(tot.getTime())) {
    return undefined;
  }
  const dagStart = new Date(vandaag.getFullYear(), vandaag.getMonth(), vandaag.getDate());
  const dagen = Math.round((tot.getTime() - dagStart.getTime()) / 86400000);
  // De dag zelf telt nog mee: wie tot en met vandaag gedekt is, is vandaag gedekt.
  if (dagen < 0) {
    return "verlopen";
  }
  return dagen <= AANDACHT_DAGEN ? "aandacht" : "inOrde";
}

/**
 * De status van de entingen als geheel.
 *
 * <p>Anders dan de andere twee, want een dier heeft meerdere soorten entingen en de api telt hoeveel er
 * verlopen zijn. Eén verlopen soort maakt het geheel verlopen: "grotendeels gedekt" is geen toestand
 * waar iemand een dier op aanneemt.
 */
export function statusVanEntingen(
  verlopen: number | null | undefined,
  geldigTot: string | null | undefined,
  vandaag: Date,
): ComponentStatus | undefined {
  if ((verlopen ?? 0) > 0) {
    return "verlopen";
  }
  return statusVanGeldigheid(geldigTot, vandaag);
}

/**
 * De toestand van één onderdeel, voor de blokjes van de halve ring.
 *
 * <p>Elk blokje moet iets zeggen, en wat het zegt verschilt per soort onderdeel:
 *
 * <ul>
 *   <li><b>Beschermingen</b> (entingen, ontworming, ontvlooiing) hebben een echte geldigheid: groen,
 *       amber of rood naar de datum.
 *   <li><b>De rest</b> kent geen geldigheid, alleen "is het vastgelegd". Groen betekent daar: het staat
 *       er. Niet groen is niet fout -- het is grijs, want er is niets bekend.
 * </ul>
 *
 * <p><b>Grijs is geen oordeel.</b> Een dier dat nooit gewogen is telt niet als probleem en niet als in
 * orde. Zou grijs meetellen als aandachtspunt, dan staat elk nieuw dier meteen in het rood; zou het
 * meetellen als in orde, dan lijkt een leeg dossier af.
 *
 * <p>Twee onderdelen kan deze functie niet beoordelen. Allergieën hebben een eigen bevraging en
 * verzorging hangt aan een boeking; beide staan grijs met "niet te bepalen" in plaats van dat de tegel
 * iets beweert wat hij niet weet.
 */
export function onderdeelStatus(
  sleutel: string,
  dier: PensionDier,
  vandaag: Date,
): ComponentStatus | "onbekend" {
  const s = dier.stand;
  switch (sleutel) {
    case "vaccinaties":
      return statusVanEntingen(s?.entingenVerlopen, s?.entingGeldigTot, vandaag) ?? "onbekend";
    case "ontworming":
      return statusVanGeldigheid(s?.ontwormingGeldigTot, vandaag) ?? "onbekend";
    case "ontvlooiing":
      return statusVanGeldigheid(s?.ontvlooiingGeldigTot, vandaag) ?? "onbekend";
    case "gewicht":
      return s?.gewichtKg != null ? "inOrde" : "onbekend";
    case "voeding":
      return s?.voer ? "inOrde" : "onbekend";
    case "medicatie":
      // Lopende medicatie is een gegeven en geen toestand; dat het vastligt is wel iets.
      return (s?.medicatieActief ?? 0) > 0 ? "inOrde" : "onbekend";
    case "gedrag":
      return dier.gedrag?.trim() ? "inOrde" : "onbekend";
    case "dierenarts":
      return dier.dierenarts?.trim() ? "inOrde" : "onbekend";
    case "dagprijs":
      return dier.prijs?.dagtariefLaag != null ? "inOrde" : "onbekend";
    case "chipPaspoort":
      return dier.chipnummer?.trim() ? "inOrde" : "onbekend";
    // De diervelden: ingevuld of niet. Er valt niets aan te laten verlopen.
    case "diersoort":
      return dier.diersoort?.trim() ? "inOrde" : "onbekend";
    case "ras":
      return dier.ras?.trim() ? "inOrde" : "onbekend";
    case "geslacht":
      return dier.geslacht?.trim() ? "inOrde" : "onbekend";
    case "leeftijd":
      return dier.geboortedatum ? "inOrde" : "onbekend";
    case "kenmerken":
      return dier.kenmerken.some((k) => k.trim() !== "") ? "inOrde" : "onbekend";
    case "bijzonderheden":
      return dier.bijzonderheden?.trim() ? "inOrde" : "onbekend";
    default:
      // allergieen en verzorging: zie de uitleg hierboven.
      return "onbekend";
  }
}
